import { type LucideIcon, TrendingUp, TrendingDown, Minus } from "lucide-react";
import clsx from "clsx";

interface StatCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  change?: number;
  changeLabel?: string;
  secondaryChange?: number;
  secondaryLabel?: string;
  icon?: LucideIcon;
  iconColor?: string;
  format?: "number" | "currency" | "percent";
}

function formatValue(value: string | number, format?: string): string {
  if (typeof value === "string") return value;

  switch (format) {
    case "currency":
      if (value >= 1000000) {
        return `${(value / 1000000).toFixed(1)}M Kč`;
      }
      if (value >= 1000) {
        return `${Math.round(value / 1000)}K Kč`;
      }
      return `${value.toLocaleString("cs-CZ")} Kč`;
    case "percent":
      return `${value}%`;
    default:
      return value.toLocaleString("cs-CZ");
  }
}

export default function StatCard({
  title,
  value,
  subtitle,
  change,
  changeLabel = "MoM",
  secondaryChange,
  secondaryLabel = "YoY",
  icon: Icon,
  iconColor = "text-primary-500",
  format,
}: StatCardProps) {
  const TrendIcon = change === undefined ? null : change > 0 ? TrendingUp : change < 0 ? TrendingDown : Minus;
  const trendColor = change === undefined ? "" : change > 0 ? "text-green-600" : change < 0 ? "text-red-600" : "text-gray-500";

  return (
    <div className="card">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{title}</p>
          <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">
            {formatValue(value, format)}
          </p>
          {subtitle && (
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{subtitle}</p>
          )}
        </div>
        {Icon && (
          <div className={clsx("rounded-lg bg-gray-100 p-3 dark:bg-gray-800", iconColor)}>
            <Icon className="h-6 w-6" />
          </div>
        )}
      </div>

      {(change !== undefined || secondaryChange !== undefined) && (
        <div className="mt-4 flex items-center gap-4">
          {change !== undefined && (
            <div className="flex items-center gap-1">
              {TrendIcon && <TrendIcon className={clsx("h-4 w-4", trendColor)} />}
              <span className={clsx("text-sm font-medium", trendColor)}>
                {change > 0 ? "+" : ""}{change.toFixed(1)}%
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">{changeLabel}</span>
            </div>
          )}
          {secondaryChange !== undefined && (
            <div className="flex items-center gap-1">
              <span
                className={clsx(
                  "text-sm font-medium",
                  secondaryChange > 0 ? "text-green-600" : secondaryChange < 0 ? "text-red-600" : "text-gray-500"
                )}
              >
                {secondaryChange > 0 ? "+" : ""}{secondaryChange.toFixed(1)}%
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">{secondaryLabel}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
